import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Check, X, Clock, MapPin, Users, BedDouble, CarFront, UtensilsCrossed, FileText, Download } from "lucide-react";
import SEO from "../lib/seo";
import PageHero from "../components/PageHero";
import SectionHeading from "../components/SectionHeading";
import FaqAccordion from "../components/FaqAccordion";
import CtaSection from "../components/CtaSection";
import { Reveal } from "../components/Reveal";
import { getPackage, getFaqs } from "../lib/api";
import { useLead, useSettings } from "../lib/store";
import { track } from "../lib/track";
import { waLink, MSG } from "../lib/whatsapp";
import { WhatsAppIcon } from "../components/icons";
import { StatusChip } from "../components/DestinationCard";

export default function PackageDetail() {
  const { slug } = useParams();
  const [pkg, setPkg] = useState(null);
  const [failed, setFailed] = useState(false);
  const [faqs, setFaqs] = useState([]);
  const { openLead } = useLead();
  const { settings } = useSettings();

  useEffect(() => {
    setPkg(null);
    setFailed(false);
    getPackage(slug)
      .then((p) => {
        setPkg(p);
        track("view_package", { package: slug, destination: p.destination_slug });
      })
      .catch(() => setFailed(true));
    getFaqs().then((f) => setFaqs(f.filter((x) => ["Booking", "Payment", "Land Arrangement"].includes(x.category)).slice(0, 5))).catch(() => {});
  }, [slug]);

  if (failed) {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center gap-4 px-6 pt-24 text-center">
        <p className="eyebrow">404</p>
        <h1 className="font-heading text-3xl font-bold text-charcoal">Paket tidak ditemukan</h1>
        <Link className="btn btn-primary" to="/land-arrangement">Lihat Semua Paket</Link>
      </div>
    );
  }

  if (!pkg) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center pt-24">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-tosca-600 border-t-transparent" />
      </div>
    );
  }

  const quote = () => {
    track("request_quotation", { package: slug, destination: pkg.destination_slug });
    openLead({ destination: pkg.destination_name, source: `package:${slug}` });
  };

  return (
    <>
      <SEO
        title={`${pkg.title} — ${pkg.duration} | Beyond Travel`}
        description={pkg.overview}
        image={pkg.hero_image}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: pkg.title,
          description: pkg.overview,
          url: window.location.href,
          brand: { "@type": "Brand", name: "Beyond Travel" },
        }}
      />

      <PageHero
        image={pkg.hero_image}
        eyebrow={`${pkg.destination_name} · ${pkg.trip_type}`}
        title={pkg.title}
        sub={pkg.overview}
        crumb={pkg.title}
      >
        <button onClick={quote} className="btn btn-gold !px-8 !py-3.5" data-testid={`package-detail-request-quotation-${slug}`}>
          Request Quotation
        </button>
        <a
          href={waLink(settings, MSG.package(pkg.title))}
          target="_blank"
          rel="noreferrer"
          onClick={() => track("click_whatsapp", { package: slug, source: "package_hero" })}
          className="btn btn-wa !px-8 !py-3.5"
          data-testid={`package-detail-whatsapp-${slug}`}
        >
          <WhatsAppIcon className="h-5 w-5" /> Konsultasi {pkg.destination_name}
        </a>
      </PageHero>

      <section className="bg-white py-10">
        <div className="container-x grid grid-cols-2 gap-4 lg:grid-cols-4">
          {[
            { icon: MapPin, label: "Destination", value: pkg.destination_name },
            { icon: Clock, label: "Duration", value: pkg.duration },
            { icon: Users, label: "Trip Type", value: pkg.trip_type },
            { icon: FileText, label: "Status", value: pkg.status, chip: true },
          ].map((m, i) => (
            <Reveal key={m.label} delay={i * 0.08}>
              <div className="flex h-full flex-col gap-2 rounded-2xl border border-slate-100 bg-cream p-5" data-testid={`package-meta-${m.label.toLowerCase()}`}>
                <m.icon className="h-5 w-5 text-tosca-600" />
                <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">{m.label}</span>
                {m.chip ? <StatusChip status={pkg.status} testid={`package-detail-status-${slug}`} /> : <span className="font-heading text-sm font-bold text-charcoal">{m.value}</span>}
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="section bg-white pt-0">
        <div className="container-x grid gap-8 lg:grid-cols-2">
          {[
            { title: "Services Included", items: pkg.services_included, icon: Check, ok: true },
            { title: "Services Excluded", items: pkg.services_excluded, icon: X, ok: false },
          ].map((col) => (
            <Reveal key={col.title}>
              <div className="h-full rounded-3xl border border-slate-100 bg-cream p-6 sm:p-8" data-testid={`package-${col.title.toLowerCase().replace(/ /g, "-")}`}>
                <h3 className="font-heading text-lg font-bold text-charcoal">{col.title}</h3>
                <ul className="mt-5 space-y-3">
                  {col.items.map((it) => (
                    <li key={it} className="flex items-start gap-3 text-sm text-slate-600">
                      <span className={`mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full ${col.ok ? "bg-tosca-100 text-tosca-700" : "bg-slate-200 text-slate-500"}`}>
                        <col.icon className="h-3 w-3" />
                      </span>
                      {it}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="section bg-cream">
        <div className="container-x">
          <SectionHeading eyebrow="Sample Itinerary" title="Itinerary Perjalanan" />
          <Reveal delay={0.15}>
            <p className="mt-4 text-sm italic text-slate-400">Itinerary dapat disesuaikan dengan kebutuhan customer.</p>
          </Reveal>
          <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {pkg.sample_itinerary.map((day, i) => (
              <Reveal key={day.day} delay={Math.min(i * 0.07, 0.3)} className="h-full">
                <div className="h-full rounded-3xl bg-white p-6 shadow-crystal" data-testid={`package-itinerary-${slug}-day-${day.day}`}>
                  <span className="chip bg-tosca-600 text-white">Day {day.day}</span>
                  <h3 className="mt-3 font-heading text-base font-bold text-charcoal">{day.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-500">{day.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-white">
        <div className="container-x grid gap-6 lg:grid-cols-3">
          {[
            { icon: BedDouble, title: "Hotel", items: pkg.hotels },
            { icon: CarFront, title: "Transportation", text: pkg.transportation },
            { icon: UtensilsCrossed, title: "Meal", text: pkg.meals },
          ].map((b, i) => (
            <Reveal key={b.title} delay={i * 0.08} className="h-full">
              <div className="h-full rounded-3xl border border-slate-100 bg-cream p-6" data-testid={`package-${b.title.toLowerCase()}-${slug}`}>
                <b.icon className="h-6 w-6 text-tosca-600" />
                <h3 className="mt-3 font-heading text-base font-bold text-charcoal">{b.title}</h3>
                {b.items ? (
                  <ul className="mt-3 space-y-2">
                    {b.items.map((it) => (
                      <li key={it} className="flex items-start gap-2 text-sm text-slate-500">
                        <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-gold" /> {it}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-3 text-sm leading-relaxed text-slate-500">{b.text}</p>
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="section bg-cream">
        <div className="container-x">
          <Reveal>
            <div className="flex flex-col items-center gap-6 rounded-3xl bg-tosca-600 p-8 text-center shadow-[0_20px_50px_-20px_rgba(15,118,110,0.6)] sm:p-12" data-testid="package-price-block">
              <p className="eyebrow !text-gold">Starting From</p>
              <h3 className="font-heading text-2xl font-extrabold text-white sm:text-3xl">Request Quotation</h3>
              <p className="max-w-md text-sm text-white/75">Hubungi kami untuk harga terbaru sesuai tanggal, jumlah peserta, dan kategori layanan pilihan Anda.</p>
              <div className="flex flex-col gap-3 sm:flex-row">
                <button onClick={quote} className="btn btn-gold !px-8 !py-3.5" data-testid="package-price-quotation-button">Request Quotation</button>
                <a
                  href={waLink(settings, MSG.package(pkg.title))}
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-wa !px-8 !py-3.5"
                  data-testid="package-price-whatsapp-button"
                >
                  <WhatsAppIcon className="h-5 w-5" /> WhatsApp
                </a>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/packages/${slug}/itinerary.pdf`}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => track("download_itinerary", { package: slug, destination: pkg.destination_slug })}
                  className="btn btn-outline-white !px-8 !py-3.5"
                  data-testid="package-download-itinerary-button"
                >
                  <Download className="h-5 w-5" /> Download Itinerary
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="section bg-white">
        <div className="container-x max-w-4xl">
          <SectionHeading eyebrow="Terms & Conditions" title="Syarat & Ketentuan" />
          <ul className="mt-8 space-y-3">
            {pkg.terms.map((t, i) => (
              <Reveal key={t} delay={Math.min(i * 0.06, 0.3)}>
                <li className="flex items-start gap-3 rounded-2xl border border-slate-100 bg-cream p-4 text-sm text-slate-600">
                  <span className="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-gold-light font-heading text-[10px] font-black text-gold-dark">{i + 1}</span>
                  {t}
                </li>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      {faqs.length > 0 && (
        <section className="section bg-cream">
          <div className="container-x max-w-4xl">
            <SectionHeading eyebrow="FAQ" title="Pertanyaan Seputar Booking" align="center" />
            <div className="mt-8">
              <FaqAccordion items={faqs} />
            </div>
          </div>
        </section>
      )}

      <CtaSection
        title={`Ready to Explore ${pkg.destination_name}?`}
        sub="Dapatkan quotation Land Arrangement sesuai kebutuhan perjalanan Anda."
        testid={`cta-package-${slug}`}
      />

      <div className="fixed inset-x-0 bottom-0 z-40 hidden md:block">
        <div className="glass mx-auto flex max-w-7xl items-center justify-between gap-6 rounded-t-3xl border-b-0 px-8 py-4">
          <div>
            <p className="font-heading text-base font-bold text-charcoal">{pkg.title}</p>
            <p className="text-xs text-slate-500">{pkg.duration} · {pkg.price_note}</p>
          </div>
          <div className="flex gap-3">
            <button onClick={quote} className="btn btn-primary" data-testid="package-sticky-quotation-button">Request Quotation</button>
            <a
              href={waLink(settings, MSG.package(pkg.title))}
              target="_blank"
              rel="noreferrer"
              className="btn btn-wa"
              data-testid="package-sticky-whatsapp-button"
            >
              <WhatsAppIcon className="h-4 w-4" /> WhatsApp
            </a>
          </div>
        </div>
      </div>
      <div className="hidden h-24 md:block" />
    </>
  );
}
